import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import {
  useCustomerTasks,
  useRemoveCustomerTask,
  useSetCustomerTaskStatus,
} from "../hooks";
import { useMe } from "../../users/hooks/useMe";
import { CustomerTaskStatus } from "../../../__generated__/globalTypes";
import { canRemoveTask } from "../permissions";
import { customerTaskStatusLabel, customerTaskPriorityInfo } from "../../../helpers/enum-labels";
import { formatDisplayDate } from "../../../helpers/date";
import { dueDateStatus, DUE_DATE_STYLES, DUE_DATE_LABELS } from "../due-date";

interface IProps {
  customerId: number;
}

export const CustomerTasksList: React.FC<IProps> = ({ customerId }) => {
  const navigate = useNavigate();
  const { data, loading, refetch } = useCustomerTasks({ where: { customerId } });
  const { data: meData } = useMe();
  const { setStatus } = useSetCustomerTaskStatus();
  const [remove] = useRemoveCustomerTask();

  const me = meData?.me;
  const tasks = data?.customerTasks?.results ?? [];
  // La colonne n'apparaît que si l'utilisateur peut agir sur au moins une ligne.
  const showActions = tasks.some((task) => canRemoveTask(task, me));

  const handleRemove = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette tâche ? Cette action est irréversible.")) return;
    try {
      const { data } = await remove({ variables: { id } });
      if (data?.removeCustomerTask?.ok) {
        toast.success("La tâche a été supprimée");
        refetch();
      } else {
        toast.error(data?.removeCustomerTask?.error ?? "Une erreur est survenue");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Une erreur est survenue");
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>, id: number) => {
    e.stopPropagation();
    setStatus(id, e.target.value as CustomerTaskStatus);
  };

  if (loading) return <Loading />;

  if (!tasks.length) {
    return <EmptyList text="Aucune tâche" />;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow hover={false}>
          <TableCell header>Titre</TableCell>
          <TableCell header align="center">
            Priorité
          </TableCell>
          <TableCell header align="center">
            Échéance
          </TableCell>
          <TableCell header>Assigné à</TableCell>
          <TableCell header>Statut</TableCell>
          {showActions && (
            <TableCell header align="center">
              Actions
            </TableCell>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {tasks.map((task) => {
          const priority = customerTaskPriorityInfo(task.priority);
          // Une tâche terminée n'est jamais signalée en retard.
          const due =
            task.status === CustomerTaskStatus.Done
              ? "none"
              : dueDateStatus(task.dueDate);
          return (
            <TableRow
              key={`customer-task-${task.id}`}
              onClick={() => navigate(`/customer-task/${task.id}/update`)}
            >
              <TableCell className="max-w-xs truncate">{task.title}</TableCell>
              <TableCell align="center">
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${priority.color}`}
                >
                  {priority.label}
                </span>
              </TableCell>
              <TableCell align="center">
                {task.dueDate ? (
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      due === "none"
                        ? "bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-400"
                        : DUE_DATE_STYLES[due]
                    }`}
                    title={due === "none" ? undefined : DUE_DATE_LABELS[due]}
                  >
                    {formatDisplayDate(task.dueDate)}
                  </span>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </TableCell>
              <TableCell>
                {task.assignedTo
                  ? `${task.assignedTo.firstname} ${task.assignedTo.lastname}`
                  : "-"}
              </TableCell>
              <TableCell>
                <select
                  className="input"
                  value={task.status}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleStatusChange(e, task.id)}
                >
                  {Object.keys(CustomerTaskStatus).map((key) => (
                    <option key={key} value={key}>
                      {customerTaskStatusLabel(key)}
                    </option>
                  ))}
                </select>
              </TableCell>
              {showActions && (
                <TableCell align="center">
                  {canRemoveTask(task, me) && (
                    <span
                      className="text-red-600 text-sm cursor-pointer hover:underline"
                      onClick={(e) => handleRemove(e, task.id)}
                    >
                      Supprimer
                    </span>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
